import { User } from "@src/state/currentUserState";

declare global {
  interface Window {
    pendo?: {
      initialize: (options: {
        visitor: {
          id: string;
          email?: string;
          firstName?: string;
          lastName?: string;
          userPermissions?: Array<{
            role: string;
            permission: string;
            level: string;
          }>;
          role?: string;
        };
        account: {
          id: string;
          accountName?: string;
          payingStatus?: string;
        };
      }) => void;
      identify?: (visitorId: string, accountId: string) => void;
      updateOptions?: (options: any) => void;
      pageLoad?: () => void;
      track?: (eventName: string, metadata?: any) => void;
      trackAgent?: (eventName: string, metadata?: any) => void;
      _q?: any[];
    };
  }
}

/**
 * Load Pendo script if not already loaded
 * Gets API key from window.djangoConfig.PENDO_API_KEY
 */
export function loadPendoScript(): void {
  if (typeof window === "undefined") {
    return;
  }

  // Get API key from Django config (passed via template)
  const apiKey = (window as any).djangoConfig?.PENDO_API_KEY;

  if (!apiKey) {
    console.warn("[Pendo] API key not found. Pendo will not be loaded.");
    return;
  }

  // Load Pendo script
  (function (key) {
    (function (p, e, n, d, o) {
      var v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      v = [
        "initialize",
        "identify",
        "updateOptions",
        "pageLoad",
        "track",
        "trackAgent",
      ];
      for (w = 0, x = v.length; w < x; ++w)
        (function (m) {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? "unshift" : "push"](
                [m].concat([].slice.call(arguments, 0)),
              );
            };
        })(v[w]);
      y = e.createElement(n);
      y.async = !0;
      y.src = "https://cdn.pendo.io/agent/static/" + key + "/pendo.js";
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, "script", "pendo");
  })(apiKey);
}

/**
 * Initialize Pendo with user and account information
 * Should be called after user authentication
 * Handles null/undefined values gracefully
 * Only initializes in production environment when API key is available
 */
export function initializePendo(user: User): void {
  // Check if window is available (client-side)
  if (typeof window === "undefined") {
    return;
  }

  // Only initialize in production
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  // Check if API key is available
  const apiKey = (window as any).djangoConfig?.PENDO_API_KEY;
  if (!apiKey) {
    console.warn("[Pendo] API key not found. Skipping initialization.");
    return;
  }

  // Build visitor object with only defined, non-empty values
  const visitor: any = {
    id: user.id,
  };

  // Only add fields if they have actual values (not null, undefined, or empty string)
  if (user.email && user.email.trim()) {
    visitor.email = user.email.trim();
  }
  if (user.firstName && user.firstName.trim()) {
    visitor.firstName = user.firstName.trim();
  }
  if (user.lastName && user.lastName.trim()) {
    visitor.lastName = user.lastName.trim();
  }
  if (user.permissions && user.permissions.length > 0) {
    visitor.userPermissions = user.permissions;
  }
  if (user.role) {
    visitor.role = user.role;
  }

  // Build account object
  const account: any = {
    id: user.activeClinic?.name || user.id,
  };
  if (user.activeClinic?.name && user.activeClinic.name.trim()) {
    account.accountName = user.activeClinic.name.trim();
  } else if (user.fullName && user.fullName.trim()) {
    account.accountName = user.fullName.trim();
  }
  // Add paying status logic if available in the future

  const pendoConfig = {
    visitor,
    account,
  };

  // Debug logging (remove in production if desired)
  console.log("[Pendo] Initializing with config:", pendoConfig);

  // Check if Pendo is loaded
  if (window.pendo && typeof window.pendo.initialize === "function") {
    try {
      window.pendo.initialize(pendoConfig);
      console.log("[Pendo] Successfully initialized");
    } catch (error) {
      console.error("[Pendo] Error initializing:", error);
    }
  } else {
    // Pendo script hasn't loaded yet, queue the initialization
    // The Pendo script will process queued calls when it loads
    console.log("[Pendo] Script not loaded yet, queuing initialization");
    if (!window.pendo) {
      window.pendo = { _q: [] };
    }
    if (!window.pendo._q) {
      window.pendo._q = [];
    }
    window.pendo._q.push(["initialize", pendoConfig]);
    console.log("[Pendo] Queued config:", pendoConfig);
  }
}
